// The stability catalog: an ICH stability study report. Its counterpart on the
// backend is the `STABILITY` entry in services/cer/templates.js.
//
// Deliberately spare next to the CER's. A stability study has no `[sota]` table, no
// device family, and no screened literature — four document lists and seven facts
// across two tables is the whole configuration.
import { GENERAL_SECTION } from "../fields";

// The study steps. Unlike a CER's sections these are not selectable, and the first
// three are not optional either: each retrieves *only* from its own list, with no
// union rule and no fallback, so an empty list is a step with no sources and the
// adapter refuses the run outright. `container` is the exception — see below.
const SECTION_CATALOG = [
    {
        key: "specification",
        label: "Specification",
        description:
            "The test listing, with its limits and method references. Usually the specification or the protocol.",
    },
    {
        key: "protocol",
        label: "Test Schedule (Protocol)",
        description: "The test schedule and its notes.",
    },
    {
        key: "report",
        label: "Study Results (Report)",
        description:
            "The study tables — results, batch headers, abbreviations. The protocol commonly belongs here too: the schedule is in the protocol, but batch identity and a conditional rule's alert level are often stated only in the report.",
    },
    {
        key: "container",
        label: "Container Closure System",
        description: "The container-closure system and the proposed shelf life.",
    },
];

// The one step that may carry nothing. A client whose packaging is described inside
// the stability report is a real case, so an empty list is accepted upstream with a
// warning rather than refused.
const OPTIONAL_SECTIONS = new Set(["container"]);

const SECTIONS = SECTION_CATALOG.map((s) => s.key);

// One step. There is nothing to select — all four sections are always generated —
// so the wizard opens straight on the form.
const STEPS = [{ key: "details", title: "Configure" }];

const BLOCK_TITLES = {
    "general:study": "The study",
};

const BLOCK_DESCRIPTIONS = {
    "general:study":
        "The study's own attributes. Every one of these changes the generated document, so an unrecognised name here is rejected outright rather than quietly ignored.",
};

// `name` is the engine's own field name. Every one of them is a
// `[stability_data]` key and carries `group: "stability_data"`, which is what
// routes it into that config table instead of into the metadata bag — this
// template asks for nothing out of `[metadata]` at all.
const FIELDS = [
    {
        section: GENERAL_SECTION,
        block: "study",
        group: "stability_data",
        name: "product_names",
        label: "Product Name(s)",
        type: "string_list",
        description:
            "The products the study covers. The engine labels the rendered tables with them.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "study",
        group: "stability_data",
        name: "purpose_of_study",
        label: "Purpose of Study",
        type: "long_text",
        description:
            "The study's scope statement, a paragraph, rendered into the document near-verbatim — write it as it should read there.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "study",
        group: "stability_data",
        name: "test_conditions",
        label: "Test Conditions",
        type: "string_list",
        description: "Leave empty for a study with one implicit condition.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "study",
        group: "stability_data",
        // `addition_tests` on the wire, and it is not a typo — see the backend
        // template. The label is what the user reads; this is what the engine does.
        name: "addition_tests",
        label: "Additional Tests",
        type: "string_list",
        description: "Tests beyond those the specification lists. Empty means none.",
    },
    {
        section: GENERAL_SECTION,
        block: "protocol",
        group: "stability_protocol",
        name: "product_type",
        label: "Submission Type",
        type: "text",
        description:
            'Specify how the regulatory filing should be referenced in the generated text, e.g. "This variation contains stability data...". Common values: Supplement, Submission, Application, Amendment, Variation.',
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "protocol",
        group: "stability_protocol",
        name: "company_name",
        label: "Company Name",
        type: "text",
        description: "The sponsor. No client document names its own sponsor.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "protocol",
        group: "stability_protocol",
        name: "extension_of_expiration_statement",
        label: "Extension of Expiration Dating Period",
        type: "long_text",
        description:
            "The extension-of-expiration commitment. Becomes its own section — leave it blank and that section is not rendered at all.",
    },
];

export default {
    id: "stability",
    steps: STEPS,
    sectionCatalog: SECTION_CATALOG,
    fields: FIELDS,
    blockTitles: BLOCK_TITLES,
    blockSections: {},
    blockDescriptions: BLOCK_DESCRIPTIONS,
    // Nothing here is scoped by screened literature.
    sectionArticleType: {},
    sectionsWithoutDocuments: new Set(),
    optionalSections: OPTIONAL_SECTIONS,
    orderedSectionKey: null,
    nonMetadataKeys: new Set(),
    // All four, always: the section picker is skipped and these are treated as
    // selected from the first render, so the config always carries all four keys
    // and the backend can hold the run to them.
    fixedSections: SECTIONS,
    payloadTables: ["stability_data", "stability_protocol"],
    // Both are a 400 under this template.
    emitsDeviceFamily: false,
    hasArticles: false,
    advisories: null,
};
